import type { LandingPageOverride } from '../../../types/campaign';
import type { LandingPageTemplate } from '../../../types/franchiseProfile';
import {
  flattenInherit,
  resolveMasterLanding,
  resolvePlatformLanding,
} from '../landingPage';
import { LandingPagePreview } from './LandingPagePreview';
import { PlaceholderHint } from './PlaceholderHint';

// Per-platform override editor used in the Google and Meta sections.
// Default = 'inherit' which reuses the Overview master. Custom requires a URL
// (validated server-side at save time; rendered here as a text input).

type OverrideType = LandingPageOverride['type'];

const OPTIONS: Array<{ value: OverrideType; label: string }> = [
  { value: 'inherit', label: 'Inherit from Overview' },
  { value: 'offer', label: 'Brand offer template' },
  { value: 'sale', label: 'Brand sale template' },
  { value: 'location', label: 'Brand location template' },
  { value: 'custom', label: 'Custom URL' },
];

export type LandingPageOverrideEditorProps = {
  label: string;
  value: LandingPageOverride;
  onChange: (next: LandingPageOverride) => void;
  // Master (Overview) selection, needed to compute the effective URL when
  // the override is 'inherit'.
  masterType: 'offer' | 'sale' | 'location' | 'custom';
  masterCustomUrl: string | null;
  // Brand templates (from FranchiseProfile). Null if the brand has no profile
  // -- every non-custom variant previews as "empty".
  templates: LandingPageTemplate | null;
};

function setMode(current: LandingPageOverride, mode: OverrideType): LandingPageOverride {
  if (mode === 'inherit') return { type: 'inherit' };
  if (mode === 'custom') {
    return current.type === 'custom'
      ? current
      : { type: 'custom', customUrl: '' };
  }
  return { type: mode };
}

export function LandingPageOverrideEditor({
  label,
  value,
  onChange,
  masterType,
  masterCustomUrl,
  templates,
}: LandingPageOverrideEditorProps) {
  const master = resolveMasterLanding(masterType, masterCustomUrl, templates);
  const platform = resolvePlatformLanding(value, masterType, masterCustomUrl, templates);
  const effective = flattenInherit(platform, master);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-900">{label}</label>
      <select
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        value={value.type}
        onChange={(e) => onChange(setMode(value, e.target.value as OverrideType))}
      >
        {OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {value.type === 'custom' && (
        <div className="space-y-1">
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.customUrl}
            onChange={(e) => onChange({ type: 'custom', customUrl: e.target.value })}
            placeholder="https://example.com/offer/{suburbSlug}"
          />
          <PlaceholderHint />
        </div>
      )}

      <LandingPagePreview resolved={value.type === 'inherit' ? effective : platform} />
    </div>
  );
}
